import apiClient from "./apiClient";

export interface Plan {
  _id?: string;
  name: string;
  tag?: string;
  description?: string;
  price: number;
  durationValue: number;
  durationUnit: string;
  features: string[];
  buttonText: string;
  isPopular?: boolean;
  isCustom?: boolean;
  icon?: string;
}

const STORAGE_KEY = "plans";

export const getStoredPlans = (): Plan[] | null => {
  try {
    const item = localStorage.getItem(STORAGE_KEY);
    return item ? JSON.parse(item) : null;
  } catch (e) {
    console.error("Error reading plans from localStorage", e);
    return null;
  }
};

export const savePlansToLocalStorage = (plans: Plan[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
  } catch (e) {
    console.error("Error saving plans to localStorage", e);
  }
};

export const addPlanToLocalStorage = (newPlan: Plan): void => {
  const plans = getStoredPlans() || [];
  const existingIdx = plans.findIndex(
    (p) => (newPlan._id && p._id === newPlan._id) || p.name === newPlan.name
  );
  if (existingIdx >= 0) {
    plans[existingIdx] = { ...plans[existingIdx], ...newPlan };
  } else {
    plans.push(newPlan);
  }
  savePlansToLocalStorage(plans);
};

export const updatePlanInLocalStorage = (
  updatedPlan: Partial<Plan> & { _id?: string; name?: string }
): void => {
  const plans = getStoredPlans() || [];
  const existingIdx = plans.findIndex(
    (p) =>
      (updatedPlan._id && p._id === updatedPlan._id) ||
      (updatedPlan.name && p.name === updatedPlan.name)
  );
  if (existingIdx >= 0) {
    plans[existingIdx] = { ...plans[existingIdx], ...updatedPlan };
    savePlansToLocalStorage(plans);
  }
};

export const deletePlanFromLocalStorage = (idOrName: string): void => {
  const plans = getStoredPlans() || [];
  const filtered = plans.filter(
    (p) => p._id !== idOrName && p.name !== idOrName
  );
  savePlansToLocalStorage(filtered);
};

export const getPlans = async (
  forceRefresh = false
): Promise<{ success: boolean; data: Plan[] }> => {
  if (!forceRefresh) {
    const cachedPlans = getStoredPlans();
    if (cachedPlans && cachedPlans.length > 0) {
      return { success: true, data: cachedPlans };
    }
  }

  try {
    const response = await apiClient.get("/plans");
    if (response.data && response.data.success && Array.isArray(response.data.data)) {
      savePlansToLocalStorage(response.data.data);
      return response.data;
    }
    const cached = getStoredPlans();
    if (cached && cached.length > 0) {
      return { success: true, data: cached };
    }
    return response.data;
  } catch (error) {
    const cached = getStoredPlans();
    if (cached && cached.length > 0) {
      return { success: true, data: cached };
    }
    throw error;
  }
};
